import { z } from "zod";

import { paginatedSchema, paginationQuerySchema, searchQuerySchema } from "../common/pagination";

export const ORGANIZATION_STATUSES = ["ACTIVE", "SUSPENDED"] as const;
export const organizationStatusSchema = z.enum(ORGANIZATION_STATUSES);
export type OrganizationStatus = z.infer<typeof organizationStatusSchema>;

/**
 * Argentine tax id, stored canonically as 99-99999999-9.
 *
 * Only the shape is enforced. CUIT carries a mod-11 check digit, but rejecting
 * input on a verification digit we have not validated against the AFIP
 * specification would block real customers over a rule we only half know, so
 * that check is deliberately left out and marked to verify.
 */
export const CUIT_PATTERN = /^\d{2}-\d{8}-\d$/;

export const cuitSchema = z
  .string()
  .trim()
  // Accept it typed with or without separators, then normalize.
  .transform((value) => value.replace(/[\s.-]/g, ""))
  .refine((digits) => /^\d{11}$/.test(digits), {
    message: "CUIT must have 11 digits.",
  })
  .transform((digits) => `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`);

/**
 * IANA time zone. Validated against the runtime's own database rather than a
 * hardcoded list, which would rot with every tzdata release.
 */
export const timezoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine(
    (value) => {
      try {
        new Intl.DateTimeFormat("en-US", { timeZone: value });

        return true;
      } catch {
        return false;
      }
    },
    { message: "Unknown time zone." },
  );

export const organizationSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  cuit: z.string().nullable(),
  status: organizationStatusSchema,
  timezone: z.string(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  /** Counts the UI shows next to each organization, so it needs no extra call. */
  counts: z.object({
    users: z.number().int().nonnegative(),
    sites: z.number().int().nonnegative(),
    devices: z.number().int().nonnegative(),
  }),
});

export type Organization = z.infer<typeof organizationSchema>;

export const createOrganizationRequestSchema = z.object({
  name: z.string().trim().min(2).max(120),
  cuit: cuitSchema.optional(),
  timezone: timezoneSchema.default("America/Argentina/Cordoba"),
});

export type CreateOrganizationRequest = z.infer<typeof createOrganizationRequestSchema>;

/**
 * Suspension is an operational decision we take, not something a customer
 * triggers, so the reason is for our own audit trail rather than for display.
 */
export const suspendOrganizationRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500).optional(),
});

export type SuspendOrganizationRequest = z.infer<typeof suspendOrganizationRequestSchema>;

export const listOrganizationsQuerySchema = paginationQuerySchema
  .extend(searchQuerySchema.shape)
  .extend({ status: organizationStatusSchema.optional() });

export type ListOrganizationsQuery = z.infer<typeof listOrganizationsQuerySchema>;

export const organizationListSchema = paginatedSchema(organizationSchema);
export type OrganizationList = z.infer<typeof organizationListSchema>;
